import { Box, Heading, Icon, Tag } from '@zillow/constellation-10';
import { IconAwardRibbonFilled } from '@zillow/constellation-icons';
import React from 'react';

import type { ExpertiseProps } from './AuthorPage.types';

/**
 * Displays a list of expertise tags for the author.
 */
const Expertise: React.FC<ExpertiseProps> = ({ expertise }) => {
    return (
        <Box
            css={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'tighter',
                md: {
                    alignItems: 'center',
                },
            }}
        >
            {/* v8 <Text as="h5" fontType="h5"> → Heading level={5} with
                textStyle heading-xs (v8 h5 = 20px, v10 heading-xs = 20px).
                textStyle is set through css rather than the prop so that every
                textStyle on this page is declared the same way — the responsive
                one in AuthorMainContent has to live in css, because the prop
                takes a single token and the only responsive heading token,
                responsive.heading-md, is a three-step 28/32/36 ramp that does
                not reproduce v8's two-step 24→36. Both forms are equivalent:
                Heading merges them in one css() call, css(headingRecipe.raw({
                textStyle }), cssProp), so the later object wins and the emitted
                class list is identical either way.

                v8 fontColor="blue600" is SkunkWorks #001962, which is v10
                blue.900 exactly (see COLOR_MAP textDarkBlue). Kept on the
                primitive so it does not invert in dark mode. */}
            <Heading
                level={5}
                css={{ textStyle: 'heading-xs', color: 'blue.900' }}
            >
                {/* v8 IconAwardRibbon → v10 IconAwardRibbonFilled. The base name
                    flipped meaning between versions: v8 shipped IconAwardRibbon
                    (a single solid path — the FILLED glyph) alongside an explicit
                    IconAwardRibbonOutline. v10's constellation-icons exports only
                    IconAwardRibbonFilled and IconAwardRibbonOutline; there is no
                    bare IconAwardRibbon. So Filled is the like-for-like mapping,
                    and using Outline here would be the style change. */}
                <Icon css={{ color: 'blue.900' }}>
                    <IconAwardRibbonFilled />
                </Icon>{' '}
                Expertise
            </Heading>
            <Box
                css={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 'tighter',
                    md: {
                        justifyContent: 'center',
                    },
                    lg: {
                        justifyContent: 'flex-start',
                    },
                }}
            >
                {/* v8 appearance="info" → v10 tone="info" is the semantic
                    equivalent, but it does not survive the theme: v10's
                    Tag.info.bg resolves to bg-feedback-info-soft = #DDDDE1 and
                    Tag.info.text to text-neutral = #111116, i.e. the SAME values
                    as tone="gray" — so the pills rendered gray where v8 had a
                    blue tint (bg #DCEDFF / text #001962). No v10 tone reproduces
                    it: tone="blue" is bg-accent-blue-soft #9ACDFF, far more
                    saturated. So the palette is pinned back to the blue ramp.

                    Careful: the v8 SkunkWorks blue ramp and the v10 blue ramp are
                    NOT the same scale — v10 is offset roughly one step darker, so
                    matching by step number gives the wrong color:

                      step   v8 SkunkWorks   v10 (constellation-tokens)
                      100    #EEF6FF         #E2F0FF
                      200    #DCEDFF         #C5E2FF

                    The v8 target here is Tag.colors.info.background = #DCEDFF
                    (ThemeSkunkWorks.js). That is v8's blue200, but in v10 blue.200
                    is #C5E2FF — off by (23, 11, 0) and visibly more saturated.
                    v10 blue.100 #E2F0FF is off by only (6, 3, 0), so blue.100 is
                    the correct v10 token for v8's #DCEDFF. blue.900 is #001962 in
                    both, matching v8's info.fontColor exactly.

                    Kept on the primitives (not semantic tokens) so the pills do
                    not invert in dark mode, matching how the other blues on this
                    page are handled. tone is left as "info" to record the v8
                    mapping even though both of its colors are overridden.

                    v8 Tag uppercased its label; v10 Tag does not, so the casing
                    is restored explicitly to keep the tags reading as v8. */}
                {expertise.map((item, index) => (
                    <Tag
                        key={`${item}-${index}`}
                        tone="info"
                        size="sm"
                        css={{
                            textTransform: 'uppercase',
                            backgroundColor: 'blue.100',
                            color: 'blue.900',
                        }}
                    >
                        {item}
                    </Tag>
                ))}
            </Box>
        </Box>
    );
};

export default Expertise;
